import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: #f0f0f0;
  padding: 20px;
  border-radius: 6px;
  width: 100%;
  max-width: 400px;
  height: 600px;
  display: flex;
  flex-direction: column;
  border: 1px solid #000;
  box-shadow: 4px 4px #737373;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0px;
  right: 0px;
  background: none;
  border: none;
  cursor: pointer;

  &:hover svg path {
    stroke: #323232;
  }
`;

const HeadingWrapper = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
`;

const Heading = styled.h2`
  margin-top: 0px;
  font-family: "BlairITCBold", sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
`;

const Subheading = styled.h3`
  color: #a00;
  font-family: "BlairITCMedium", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 5px;
`;

const FormWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 320px;
`;

const Label = styled.label`
  font-family: 'BlairITCMedium', sans-serif;
  font-size: 12px;
  color: #000;
  margin-top: 15px;
`;

const Input = styled.input`
  padding: 10px;
  font-size: 14px;
  border: 1px solid ${({ hasError }) => (hasError ? 'red' : '#ccc')};
  border-radius: 4px;
  font-family: 'BlairITCMedium', sans-serif;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  width: 100%;

  &:hover {
    border-color: ${({ hasError }) => (hasError ? 'red' : '#888')};
  }

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? 'red' : '#000')};
  }
`;

const SubmitButton = styled.button`
  margin: 20px auto 10px auto;
  padding: 10px;
  width: 100%;
  background-color: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'BlairITCMedium', sans-serif;
  font-size: 16px;
  text-transform: uppercase;
  max-width: 320px;

  &:hover {
    background-color: #323232;
  }

  &:disabled {
    background-color: #737373;
    cursor: not-allowed;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  gap: 30px;
`;

const Column = styled.div`
  flex: 1;
  width: 47%;
  box-sizing: border-box;
`;

const SubmittedInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 315px;
  text-align: center;
`;

const InformationTitle = styled.h5`
  font-family: 'BlairITCMedium', sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  margin-bottom: 5px;
  margin-top: 5px;
`;

const InformationValue = styled.span`
  font-family: 'BlairITCMedium', sans-serif;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2px;
`;

const SelectedPunk = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin-top: 35px;
`;

const SelectedPunkId = styled.span`
  font-family: 'BlairITCMedium', sans-serif;
  font-size: 11px;
  font-weight: 500;
  margin-top: 10px;
`;

const ThanksContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 160px;
  margin-top: 10px;
  margin-bottom: 40px;
`;

const ThanksTitle = styled.span`
  margin-left: 65px;
  margin-right: 65px;
  text-transform: uppercase;
  font-family: 'BlairITCMedium', sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
`;

const CryptoPunkImageContainer = styled.div`
  width: 150px;
  background-color: #638596;
  border-radius: 4px;
`;

const CryptoPunkImage = styled.img`
  width: 100%;
  border-radius: 4px;
`;

const TelegramIcon = styled.div`
  justify-content: center;
  align-content: center;
  height: 50px;
  width: 50px;
  margin-top: 10px;
`;

const TelegramButton = styled.button`
  margin: 0px auto 10px auto;
  padding: 10px;
  width: 100%;
  background-color: #BD1D1D;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'BlairITCMedium', sans-serif;
  font-size: 13px;
  text-transform: uppercase;
  max-width: 320px;

  &:hover {
    background-color: #CA4A4A;
  }
`;

const isObject = (value) => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

const lowerCaseKeysOfObject = (obj) => {
  if (isObject(obj)) {
    return Object.entries(obj).reduce((carry, [key, value]) => {
      carry[key] = value.toLowerCase();
      return carry;
    }, {});
  }
};

const Modal = ({ isOpen, onClose, punkId }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    mail: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    phone: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = () => {
    setFormErrors({});
    onClose();
    setFormValues({
      firstName: '',
      lastName: '',
      mail: '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
      phone: '',
    });
    setCurrentStep(1);
  };

  const getTitleForStep = (step) => {
    switch (step) {
      case 1, 2:
        return "Personal Information";
      case 3:
        return "For Your Mint";
      default:
        return "Personal Information";
    }
  };

  const getSubTitleForStep = (step) => {
    switch (step) {
      case 1, 2:
        return "Your";
      case 3:
        return "Thank You";
      default:
        return "Your";
    }
  };

  const validateField = (name, value) => {
    let error = '';
    if (!value) {
      error = 'This field is required';
    } else if (name === 'mail' && !/\S+@\S+\.\S+/.test(value)) {
      error = 'Email address is invalid';
    } else if (name === 'phone' && !/^\+\d{1,3}\s?\d{4,14}$/.test(value)) {
      error = 'Phone number must start with an international code (e.g., +123)';
    }
    return error;
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;

    const filteredValue = value.replace(/[^+\d]/g, '');

    setFormValues({ ...formValues, phone: filteredValue });

    const error = validateField('phone', filteredValue);
    setFormErrors({ ...formErrors, phone: error });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });

    const error = validateField(name, value);
    setFormErrors({ ...formErrors, [name]: error });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    const error = validateField(name, value);
    setFormErrors({ ...formErrors, [name]: error });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (currentStep === 1) {
      const errors = Object.keys(formValues).reduce((acc, field) => {
        const error = validateField(field, formValues[field]);
        if (error) acc[field] = error;
        return acc;
      }, {});

      setFormErrors(errors);

      if (Object.keys(errors).length === 0) {
        setIsSubmitting(true);
      }
    } else if (currentStep === 2) {
      setCurrentStep(3);
    } else if (currentStep === 3) {
      closeModal();
    }
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmitting) {
      console.log('Form data:', lowerCaseKeysOfObject(formValues));
      setIsSubmitting(false);
      setCurrentStep(2);
    }
  }, [formErrors, isSubmitting, formValues]);

  const isSubmitDisabled =
    currentStep === 1 &&
    (Object.values(formValues).some(value => value.trim() === '') ||
      Object.keys(formErrors).some((key) => formErrors[key]));

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={closeModal}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={closeModal}>
          <svg width="48" height="48" viewBox="0 0 128 128">
            <path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeWidth="7" d="M46 46L82 82M81 46L46 82" />
          </svg>
        </CloseButton>
        <HeadingWrapper>
          <Subheading>{getSubTitleForStep(currentStep)}</Subheading>
          <Heading>{getTitleForStep(currentStep)}</Heading>
        </HeadingWrapper>
        <FormWrapper currentStep={currentStep}>
          {currentStep === 1 && (
            <Form>
              <Label style={{ marginTop: "10px" }}>First Name</Label>
              <Input
                type="text"
                name="firstName"
                autoComplete="given-name"
                value={formValues.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                hasError={!!formErrors.firstName}
              />
              <Label>Last Name</Label>
              <Input
                type="text"
                name="lastName"
                autoComplete="family-name"
                value={formValues.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                hasError={!!formErrors.lastName}
              />
              <Label>Mail</Label>
              <Input
                type="email"
                name="mail"
                autoComplete="email"
                value={formValues.mail}
                onChange={handleChange}
                onBlur={handleBlur}
                hasError={!!formErrors.mail}
              />
              <Label>Address</Label>
              <Input
                type="text"
                name="address"
                autoComplete="address-line1"
                value={formValues.address}
                onChange={handleChange}
                onBlur={handleBlur}
                hasError={!!formErrors.address}
              />
              <Row>
                <Column>
                  <Label>City</Label>
                  <Input
                    type="text"
                    name="city"
                    autoComplete="address-level2"
                    value={formValues.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    hasError={!!formErrors.city}
                  />
                </Column>
                <Column>
                  <Label>Postal Code</Label>
                  <Input
                    type="text"
                    name="postalCode"
                    autoComplete="postal-code"
                    value={formValues.postalCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    hasError={!!formErrors.postalCode}
                  />
                </Column>
              </Row>
              <Label>Country</Label>
              <Input
                type="text"
                name="country"
                autoComplete="country-name"
                value={formValues.country}
                onChange={handleChange}
                onBlur={handleBlur}
                hasError={!!formErrors.country}
              />
              <Label>Phone</Label>
              <Input
                type="tel"
                name="phone"
                autoComplete="tel"
                value={formValues.phone}
                onChange={handlePhoneChange}
                onBlur={handleBlur}
                hasError={!!formErrors.phone}
                placeholder="+ 123 4567890"
              />
            </Form>
          )}
          {currentStep === 2 && (
            <div>
              <SubmittedInfo>
                <InformationTitle>
                  First Name : <InformationValue>{formValues.firstName}</InformationValue>
                </InformationTitle>
                <InformationTitle>
                  Last Name : <InformationValue>{formValues.lastName}</InformationValue>
                </InformationTitle>
                <InformationTitle>
                  Email : <InformationValue>{formValues.mail}</InformationValue>
                </InformationTitle>
                <InformationTitle>
                  Address : <InformationValue>{formValues.address}</InformationValue>
                </InformationTitle>
                <InformationTitle>
                  City : <InformationValue>{formValues.city}</InformationValue>
                </InformationTitle>
                <InformationTitle>
                  Postal Code : <InformationValue>{formValues.postalCode}</InformationValue>
                </InformationTitle>
                <InformationTitle>
                  Country : <InformationValue>{formValues.country}</InformationValue>
                </InformationTitle>
                <InformationTitle>
                  Phone : <InformationValue>{formValues.phone}</InformationValue>
                </InformationTitle>
              </SubmittedInfo>
              <SelectedPunk>
                <Heading style={{ marginBottom: '10px' }}>Punk selected :</Heading>
                <CryptoPunkImageContainer>
                  <CryptoPunkImage src={`/assets/regular/punk${punkId}.png`} />
                </CryptoPunkImageContainer>
                <SelectedPunkId>#{punkId}</SelectedPunkId>
              </SelectedPunk>
            </div>
          )}
          {currentStep === 3 && (
            <div>
              <ThanksContainer>
                <ThanksTitle>Please join our telegram channel for update</ThanksTitle>
                <TelegramIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 512"><path fill="#000" d="M512 256C512 114.62 397.38 0 256 0S0 114.62 0 256s114.62 256 256 256 256-114.62 256-256zm-396.12-2.7c74.63-32.52 124.39-53.95 149.29-64.31 71.1-29.57 85.87-34.71 95.5-34.88 2.12-.03 6.85.49 9.92 2.98 2.59 2.1 3.3 4.94 3.64 6.93.34 2 .77 6.53.43 10.08-3.85 40.48-20.52 138.71-29 184.05-3.59 19.19-10.66 25.62-17.5 26.25-14.86 1.37-26.15-9.83-40.55-19.27-22.53-14.76-35.26-23.96-57.13-38.37-25.28-16.66-8.89-25.81 5.51-40.77 3.77-3.92 69.27-63.5 70.54-68.9.16-.68.31-3.2-1.19-4.53s-3.71-.87-5.3-.51c-2.26.51-38.25 24.3-107.98 71.37-10.22 7.02-19.48 10.43-27.77 10.26-9.14-.2-26.72-5.17-39.79-9.42-16.03-5.21-28.77-7.97-27.66-16.82.57-4.61 6.92-9.32 19.04-14.14z" /></svg>
                </TelegramIcon>
                <TelegramButton>Join our telegram</TelegramButton>
              </ThanksContainer>
              <SelectedPunk>
                <Heading style={{ marginBottom: '10px' }}>Punk selected :</Heading>
                <CryptoPunkImageContainer>
                  <CryptoPunkImage src={`/assets/regular/punk${punkId}.png`} />
                </CryptoPunkImageContainer>
                <SelectedPunkId>#{punkId}</SelectedPunkId>
              </SelectedPunk>
            </div>
          )}
        </FormWrapper>
        <SubmitButton
          type="submit"
          onClick={handleSubmit}
          disabled={currentStep === 1 && isSubmitDisabled}
        >
          {currentStep === 1 ? 'Continue' : currentStep === 2 ? 'Confirm' : 'Close'}
        </SubmitButton>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default Modal;
